import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { ApiError } from "@/shared/api/http-client";
import { useSubscriptionDetail } from "./use-subscription-detail";
import { SubscriptionStatusBadge } from "./SubscriptionStatusBadge";
import { formatAmount } from "./format-amount";
import type { AdminSubscriptionDetail, PriceSnapshot } from "./types";

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

interface FieldProps {
  readonly label: string;
  readonly children: ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <div className="space-y-0.5">
      <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="font-sans text-sm text-foreground">{children}</div>
    </div>
  );
}

interface SectionProps {
  readonly title: string;
  readonly testId: string;
  readonly children: ReactNode;
}

function Section({ title, testId, children }: SectionProps) {
  return (
    <section data-testid={testId} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h2 className="font-display text-lg">{title}</h2>
      {children}
    </section>
  );
}

function PlanSummary({ snapshot }: { readonly snapshot: PriceSnapshot }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label={t("subscriptions.detail.fields.price")}>
        {formatAmount(snapshot.amountCents, snapshot.currency)}{" "}
        <span className="text-muted-foreground">
          {t(`subscriptions.detail.interval.${snapshot.intervalUnit}`, {
            count: snapshot.intervalCount,
          })}
        </span>
      </Field>
      <Field label={t("subscriptions.detail.fields.stripePrice")}>
        <span className="font-mono text-xs">{snapshot.stripePriceId}</span>
      </Field>
      <Field label={t("subscriptions.detail.fields.stripeProduct")}>
        <span className="font-mono text-xs">{snapshot.stripeProductId}</span>
      </Field>
    </div>
  );
}

function DetailBody({ sub }: { readonly sub: AdminSubscriptionDetail }) {
  const { t } = useTranslation();
  const cancelAt = formatDate(sub.cancelAt);
  const pastDueAt = formatDate(sub.pastDueAt);
  const cancelledAt = formatDate(sub.cancelledAt);
  const periodStart = formatDate(sub.currentPeriodStart);
  const periodEnd = formatDate(sub.currentPeriodEnd);
  const trialEndsAt = formatDate(sub.trialEndsAt);
  const dash = t("subscriptions.detail.none");

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <Link
          to="/subscriptions"
          data-testid="subscription-detail-back"
          className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t("subscriptions.detail.back")}
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-3xl">{sub.propertyName}</h1>
          <SubscriptionStatusBadge status={sub.status} testId="subscription-detail-status" />
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 font-sans text-xs font-medium text-primary">
            {sub.tier}
          </span>
        </div>
        <p className="font-sans text-sm text-muted-foreground">{sub.ownerEmail}</p>
        <p className="font-mono text-xs text-muted-foreground">{sub.id}</p>
      </header>

      {cancelAt && (
        <p
          data-testid="subscription-detail-cancel-notice"
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 font-sans text-sm font-medium text-amber-800"
        >
          {t("subscriptions.detail.cancelNotice", { date: cancelAt })}
        </p>
      )}
      {pastDueAt && (
        <p
          data-testid="subscription-detail-pastdue-notice"
          role="status"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-sans text-sm font-medium text-destructive"
        >
          {t("subscriptions.detail.pastDueNotice", { date: pastDueAt })}
        </p>
      )}

      <Section
        title={t("subscriptions.detail.currentPlan")}
        testId="subscription-detail-current-plan"
      >
        <PlanSummary snapshot={sub.priceSnapshot} />
      </Section>

      <Section title={t("subscriptions.detail.billing")} testId="subscription-detail-billing">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("subscriptions.detail.fields.created")}>
            {formatDate(sub.createdAt) ?? dash}
          </Field>
          <Field label={t("subscriptions.detail.fields.currentPeriod")}>
            {periodStart && periodEnd
              ? t("subscriptions.detail.periodRange", {
                  start: periodStart,
                  end: periodEnd,
                })
              : dash}
          </Field>
          <Field label={t("subscriptions.detail.fields.trialEnds")}>{trialEndsAt ?? dash}</Field>
          {cancelledAt && (
            <Field label={t("subscriptions.detail.fields.cancelled")}>{cancelledAt}</Field>
          )}
        </div>
      </Section>

      {sub.pendingPriceSnapshot && (
        <Section
          title={t("subscriptions.detail.pendingChange")}
          testId="subscription-detail-pending-change"
        >
          <p className="font-sans text-sm text-muted-foreground">
            {t("subscriptions.detail.pendingEffective", {
              date: formatDate(sub.pendingPriceEffectiveAt) ?? dash,
            })}
          </p>
          <PlanSummary snapshot={sub.pendingPriceSnapshot} />
        </Section>
      )}
    </div>
  );
}

export function SubscriptionDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const { data, isLoading, isError, error } = useSubscriptionDetail(id);

  const notFound = error instanceof ApiError && error.status === 404;

  return (
    <div data-testid="subscription-detail-screen" className="space-y-6">
      {isLoading && (
        <p
          data-testid="subscription-detail-loading"
          className="font-sans text-sm text-muted-foreground"
        >
          {t("subscriptions.detail.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="subscription-detail-error"
          className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {notFound ? t("subscriptions.detail.notFound") : t("subscriptions.detail.error")}
          </p>
          <Link
            to="/subscriptions"
            className="inline-block font-sans text-sm text-primary hover:underline"
          >
            {t("subscriptions.detail.back")}
          </Link>
        </div>
      )}

      {data && <DetailBody sub={data} />}
    </div>
  );
}
